import React, { useEffect, useState, useRef } from 'react';
import isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';
// components
import AttachmentsGrid from './AttachmentsGrid';
import DragAndDropUploader from 'shared/inputs/DragAndDropUploader';
import FilesToUploadGrid from './FilesToUploadGrid';
import { GridErrorMessage } from 'shared/grids';
import { Loader } from '@progress/kendo-react-indicators';
import { Slide } from '@progress/kendo-react-animation';
import PreviewModal from 'shared/media/PreviewModal';
// hooks
import useStoreLoading from './useStoreLoading';
// consts
import { requestStatuses } from 'shared/../constants/store';
// store
import { getFileTypes } from 'src/slices/fileTypes';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const getFilesToUpload = (items, defaultDocumentType) => items.map((item, ind) => ({
  ...item,
  tempId: ind + 1,
  date: new Date(),
  documentTypeId: defaultDocumentType?.id,
  description: '',
  inEdit: true,
}));

// HACK: use grid with an inline form for the files frid as it's done

const Attachments = ({
  id,
  source,
  sourceId,
  defaultDocumentType,
  disabled,
  readOnly = false,
  onPreviewAttachment,
  showAttachmentsGrid = true,
  showDriverDoc,
  hideUploadAction,
  onChange,
  files,
  afterSave,
  name,
  ...props
}) => {
  const documentTypes = useSelector(getFileTypes);
  const attachmentsGridRef = useRef();
  const [filesToUpload, setFilesToUpload] = useState([]);
  const { status, error } = useStoreLoading(source);
  const isLoading = status === requestStatuses.INIT || status === requestStatuses.PENDING;

  const initialSort = [
    { field: 'isPinned', dir: 'desc' },
    { field: 'createdDateTime', dir: 'desc' },
  ];

  useEffect(() => {
    if (files && files.length > 0) {
      setFilesToUpload(files);
    }
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange(filesToUpload);
    }
  }, [filesToUpload]);

  const onUpload = (items) => {
    if (hideUploadAction) {
      const newDocuments = getFilesToUpload(
        items,
        documentTypes?.find(({ id }) => id === defaultDocumentType),
      );
      const documents = [...filesToUpload, ...newDocuments].map((item, ind) => ({ ...item, tempId: ind + 1 }));
      setFilesToUpload(documents);
      return;
    }
    setFilesToUpload(
      getFilesToUpload(
        items,
        documentTypes?.find(({ id }) => id === defaultDocumentType),
      ),
    );
  };

  // Preview modal
  const [previewFile, setPreviewFile] = useState(null);
  const handlePreview = onPreviewAttachment || setPreviewFile;

  const [needReload, setNeedReload] = useState(false);
  const handleReload = () => setNeedReload(true);
  const didReload = () => setNeedReload(false);

  const hasFilesToUpload = filesToUpload.length > 0;
  const canViewAttachmentGrid = !isNil(sourceId) && showAttachmentsGrid;

  const innerId = id ?? source;
  const dataTestId = `attachments-upload${innerId ? `-${innerId}` : ''}`;

  return (
    <>
      <div className="attachments-container">
        {(status === requestStatuses.INIT || status === requestStatuses.PENDING) && (
          <Loader type="converging-spinner" />
        )}
        {status === requestStatuses.FAILED && <GridErrorMessage name="Attachments" error={error} />}
        {status === requestStatuses.SUCCESS && (
          <>
            {!readOnly && (
              <>
                <section className="drag-and-drop card-fluid">
                  <DragAndDropUploader dataTestId={dataTestId} disabled={disabled || isLoading} onUpload={onUpload} />
                </section>
                <Slide direction="up" exit={false}>
                  {hasFilesToUpload && (
                    <FilesToUploadGrid
                      source={source}
                      sourceId={sourceId}
                      files={filesToUpload}
                      setFiles={setFilesToUpload}
                      handleReload={handleReload}
                      showDriverDoc={showDriverDoc}
                      hideUploadAction={hideUploadAction}
                      afterSave={afterSave}
                    />
                  )}
                </Slide>
              </>
            )}
            {canViewAttachmentGrid && (
              <AttachmentsGrid
                {...props}
                id={`${innerId ? `${innerId}-` : ''}attachments`}
                ref={attachmentsGridRef}
                needReload={needReload}
                didReload={didReload}
                handleReload={handleReload}
                disabled={disabled || isLoading}
                readOnly={readOnly}
                source={source}
                sourceId={sourceId}
                onPreview={handlePreview}
                showDriverDoc={showDriverDoc}
                afterSave={afterSave}
                initialSort={initialSort}
                name={name}
              />
            )}
          </>
        )}
      </div>
      {previewFile && <PreviewModal onClose={() => setPreviewFile(null)} previewFile={previewFile} />}
    </>
  );
};

export default Attachments;
